// Movement 2 of 4: walk the learner through each dimension in turn.
// Source: CogniPrint_Recalibration_Dev_Plan_v1.md §3.7 (REC-030), §5.3 (REC-072).

import { useState } from 'react';

interface Props {
  index: number;
  total: number;
  question: string;
  currentLabel: string;
  currentScore: number;
  chips: string[];
  userResponse: string | null;
  freeText: string | null;
  onSelectChip: (chip: string) => void;
  onFreeTextChange: (text: string) => void;
  onBack: () => void;
  onNext: () => void;
  /**
   * Coach-only: hover/tap on a non-selected chip fetches a one-sentence
   * counterfactual preview via REC-072. Callers pass a debounced async
   * fn that returns the sentence or empty string.
   */
  onHoverChip?: (alternativeChip: string) => Promise<string>;
}

export function DimensionConfirm({
  index,
  total,
  question,
  currentLabel,
  currentScore,
  chips,
  userResponse,
  freeText,
  onSelectChip,
  onFreeTextChange,
  onBack,
  onNext,
  onHoverChip,
}: Props) {
  const [counterfactual, setCounterfactual] = useState<{
    chip: string;
    sentence: string;
  } | null>(null);
  const [loadingChip, setLoadingChip] = useState<string | null>(null);

  async function handleHover(chip: string) {
    if (!onHoverChip || chip === userResponse) return;
    if (counterfactual?.chip === chip) return;
    setLoadingChip(chip);
    try {
      const sentence = await onHoverChip(chip);
      if (sentence) setCounterfactual({ chip, sentence });
    } finally {
      setLoadingChip((current) => (current === chip ? null : current));
    }
  }

  function handleLeave(chip: string) {
    if (counterfactual?.chip === chip) setCounterfactual(null);
  }

  return (
    <>
      <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
        Movement 2 · Dimension {index + 1} of {total}
      </div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-[16px] font-bold text-white leading-snug">
          {question}
        </h3>
        <div
          className="font-code text-xs rounded-md"
          style={{
            padding: '3px 8px',
            background: 'rgba(108,92,231,0.1)',
            border: '1px solid rgba(108,92,231,0.25)',
            color: '#A39BE0',
          }}
        >
          {currentLabel} · {currentScore}
        </div>
      </div>
      <div className="flex flex-col gap-2 mb-4">
        {chips.map((chip) => {
          const selected = userResponse === chip;
          return (
            <button
              key={chip}
              onClick={() => onSelectChip(chip)}
              onMouseEnter={() => handleHover(chip)}
              onMouseLeave={() => handleLeave(chip)}
              onFocus={() => handleHover(chip)}
              onBlur={() => handleLeave(chip)}
              className="text-[13px] font-medium rounded-lg text-left transition-all duration-200"
              style={{
                padding: '10px 14px',
                background: selected ? 'rgba(108,92,231,0.18)' : 'rgba(255,255,255,0.03)',
                border: selected ? '1px solid rgba(108,92,231,0.5)' : '1px solid rgba(255,255,255,0.08)',
                color: selected ? '#A39BE0' : '#c0bdd4',
              }}
            >
              {chip}
            </button>
          );
        })}
      </div>
      {(counterfactual || loadingChip) && (
        <div
          className="rounded-lg text-[12px] leading-snug mb-3 p-3"
          style={{
            background: 'rgba(240,165,0,0.06)',
            border: '1px solid rgba(240,165,0,0.18)',
            color: '#F0A500',
          }}
        >
          {loadingChip && !counterfactual
            ? 'Simulating…'
            : `If you'd choose “${counterfactual!.chip}”: ${counterfactual!.sentence}`}
        </div>
      )}
      <textarea
        placeholder="Notes — anything specific (optional)"
        value={freeText ?? ''}
        onChange={(e) => onFreeTextChange(e.target.value)}
        rows={2}
        className="w-full rounded-lg text-sm p-3 mb-5"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          color: '#e1dfef',
          resize: 'none',
        }}
      />
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-xs text-content-muted underline">
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!userResponse}
          className="rounded-lg font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            padding: '9px 18px',
            background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
            color: '#fff',
          }}
        >
          {index < total - 1 ? 'Next' : 'Continue'}
        </button>
      </div>
    </>
  );
}
